import {Title} from "./components/Title";
import "flowbite/dist/flowbite.css";
import DragAndDrop from "./components/DragAndDrop";
import AudioPlayer from "./components/AudioPlayer";
import UploadedAudio from "./components/UploadedAudio";
import {useState} from "react";
import {TranscriptionButton} from "./components/TranscriptionButton";
import TranscriptionDisplay from "./components/TranscriptionDisplay";
import {useTranslation} from "react-i18next";
import LangSwitch from "./components/LangSwitch";
import {submitFeedback} from "./api/feedback/feedback";
import AudioRecorder from "./components/AudioRecorder";
import Mixpanel from "./utils/mixpanel";
function App() {
    const [isRecording, setIsRecording] = useState(false);
    const [audioFile, setAudioFile] = useState(null);
    const [transcription, setTranscription] = useState("");
    const [audioUuid, setAudioUuid] = useState("");
    const {t} = useTranslation("global");

    const handleSubmitFeedback = async (feedback, correctedText) => {
        await submitFeedback(feedback, correctedText, audioUuid);
        Mixpanel.track("Feedback submitted", {
            feedback,
            transcription: correctedText,
            oldTranscription: transcription,
            audioUuid,
        });
    };

    const getFileExtension = (file) => {
        return file.name.split(".").pop();
    };

    const getFileSize = (file) => {
        return (file.size / (1024 * 1024)).toFixed(2).toString();
    };

    const getFileName = (file) => {
        return file.name;
    };

    const getFileDuration = async (file) => {
        return new Promise((resolve) => {
            const audio = new Audio(URL.createObjectURL(file));
            audio.addEventListener("loadedmetadata", () => {
                const duration = audio.duration;
                const duration_minutes = Math.floor(duration / 60);
                const duration_seconds = Math.floor(duration % 60);
                URL.revokeObjectURL(audio.src);
                resolve({
                    duration_minutes,
                    duration_seconds,
                    duration,
                });
            });
        });
    };

    const handleFileUpload = async (file) => {
        if (file) {
            if (file.length > 0) {
                setAudioFile(file[0]);
                setTranscription("");
                const {duration_minutes, duration_seconds, duration} =
                    await getFileDuration(file[0]);
                Mixpanel.track("Uploaded file", {
                    fileExtension: getFileExtension(file[0]),
                    fileSize: getFileSize(file[0]),
                    fileName: getFileName(file[0]),
                    file_minutes: duration_minutes,
                    file_seconds: duration_seconds,
                    file_duration: duration,
                });
            } else {
                setAudioFile(file);
                setTranscription("");
                const {duration_minutes, duration_seconds, duration} =
                    await getFileDuration(file);
                Mixpanel.track("Uploaded file", {
                    fileExtension: getFileExtension(file),
                    fileSize: getFileSize(file),
                    fileName: getFileName(file),
                    file_minutes: duration_minutes,
                    file_seconds: duration_seconds,
                    file_duration: duration,
                });
            }
        } else {
            console.error("Invalid file object received");
            Mixpanel.track("Invalid file uploaded", {
                error: "Invalid file object",
            });
        }
    };

    const handleDelete = () => {
        setAudioFile(null);
        setIsRecording(false);
        setTranscription("");
        Mixpanel.track("Removed file");
    };

    const handleTranscriptionComplete = (result, audioUuid) => {
        setTranscription(result);
        setAudioUuid(audioUuid);
        Mixpanel.track("Transcription complete", {
            transcription: result,
            audioUuid: audioUuid,
        });
    };

    return (
        <div className="min-h-screen w-full bg-gradient-to-br from-indigo-50 to-purple-50 p-4 sm:p-6 md:p-8">
            <LangSwitch />

            <div className="flex flex-col items-center justify-start w-full gap-6 sm:gap-8 md:gap-12">
                <Title text={t("mainSection.title")} />

                {/* Main content container */}
                <div className="w-full max-w-4xl bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl p-4 sm:p-6 md:p-8">
                    {!audioFile && (
                        <h1 className="text-2xl font-semibold text-indigo-700 mb-4">
                            {t("input.title")}
                        </h1>
                    )}
                    <div className="w-full flex flex-col gap-4">
                        {!audioFile && !isRecording && (
                            <>
                                <h2 className="w-full text-xl font-semibold text-indigo-700">
                                    {t("upload.title")}
                                </h2>
                                <p className="w-full text-gray-600 text-sm">
                                    {t("upload.description")}
                                </p>
                            </>
                        )}
                        {/* Show upload section only if there's no transcription */}
                        {!transcription && !audioFile && !isRecording && (
                            <>
                                <div className="w-full">
                                    <DragAndDrop
                                        onFileUpload={handleFileUpload}
                                    />
                                </div>
                            </>
                        )}
                        {/* Audio Recorder Section */}
                        {!audioFile && (
                            <div className="w-full flex flex-col items-center gap-4">
                                <h2 className="w-full text-xl font-semibold text-indigo-700">
                                    {t("recorder.title")}
                                </h2>
                                <p className="w-full text-gray-600 text-sm">
                                    {t("recorder.description")}
                                </p>
                                <AudioRecorder
                                    onRecordingComplete={handleFileUpload}
                                    onRecordingStart={() => {
                                        setIsRecording(true);
                                        Mixpanel.track("Recording started");
                                    }}
                                    onDelete={handleDelete}
                                />
                            </div>
                        )}

                        {/* Audio Player Section - Always visible when there's an audio file */}
                        {audioFile && (
                            <div className="w-full flex flex-col items-center gap-6 ">
                                <div className="w-full flex flex-col items-center justify-start text-left">
                                    <h2 className="w-full text-2xl font-semibold text-indigo-700 mb-4">
                                        {t("uploaded.title")}
                                    </h2>
                                    <div className="w-full mb-4">
                                        <UploadedAudio
                                            audioFile={audioFile}
                                            processed={
                                                transcription ? true : false
                                            }
                                            onDelete={handleDelete}
                                            onTranscriptionComplete={
                                                handleTranscriptionComplete
                                            }
                                        />
                                    </div>
                                    {!transcription && (
                                        <p className="w-full text-gray-600 text-sm">
                                            {t("uploaded.description")}
                                        </p>
                                    )}
                                </div>
                                <AudioPlayer audioFile={audioFile} />
                                {/* Show CustomButton only if there's no transcription yet */}
                                {!transcription && (
                                    <TranscriptionButton
                                        audioFile={audioFile}
                                        onTranscriptionComplete={
                                            handleTranscriptionComplete
                                        }
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Transcription Results Section */}
                {transcription && (
                    <div className="w-full max-w-4xl flex flex-col items-center gap-4">
                        <TranscriptionDisplay
                            transcription={transcription}
                            onSubmitFeedback={handleSubmitFeedback}
                        />
                        <button
                            onClick={() => {
                                handleDelete();
                                Mixpanel.track("New transcription");
                            }}
                            className="mt-4 px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full 
                                   font-semibold shadow-lg hover:shadow-xl transition-all duration-300
                                   hover:scale-105 active:scale-95"
                        >
                            {t("restart.title")}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default App;
